import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
	ProgramOwnerCheckoutStatesSupportedDto,
	ServicesProvider,
} from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to update checkout states supported configuration for a services provider
 */
export const updateCheckoutStatesSupported = async (
	servicesProviderId: string,
	data: ProgramOwnerCheckoutStatesSupportedDto,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.checkoutStatesSupported(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for updating checkout states supported configuration for a services provider
 */
export const useUpdateCheckoutStatesSupported = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: ProgramOwnerCheckoutStatesSupportedDto) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to update checkout states supported without a services provider ID.',
				);
			}
			return updateCheckoutStatesSupported(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(queries.servicesProviders.detail(data.id).queryKey, data);
		},
	});
};
